import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Check, ArrowUp, ArrowDown } from 'lucide-react';
import type { SortByKey, SortConfig } from '@/hooks/useSortedSimulations.ts';

interface SortOption {
  key: SortByKey;
  label: string;
  ascLabel: string;
  descLabel: string;
}

interface SortBySelectorProps {
  value: SortConfig;
  onChange: (config: SortConfig) => void;
  /** When provided, only these keys are shown. Defaults to all options. */
  availableKeys?: SortByKey[];
}

export default function SortBySelector({ value, onChange, availableKeys }: SortBySelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const options: SortOption[] = useMemo(() => [
    { key: 'default', label: t('sortBy.default'), ascLabel: '', descLabel: '' },
    { key: 'name', label: t('sortBy.name'), ascLabel: t('sortBy.nameAsc'), descLabel: t('sortBy.nameDesc') },
    { key: 'difficultyLevel', label: t('sortBy.difficulty'), ascLabel: t('sortBy.difficultyAsc'), descLabel: t('sortBy.difficultyDesc') },
    { key: 'priceRange', label: t('sortBy.priceRange'), ascLabel: t('sortBy.priceAsc'), descLabel: t('sortBy.priceDesc') },
    { key: 'createdAt', label: t('sortBy.createdAt'), ascLabel: t('sortBy.dateAsc'), descLabel: t('sortBy.dateDesc') },
  ], [t]);

  const visibleOptions = availableKeys
    ? options.filter((o) => availableKeys.includes(o.key))
    : options;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isActive = value.key !== 'default';

  const handleSelect = (key: SortByKey) => {
    if (key === 'default') {
      onChange({ key: 'default', direction: 'asc' });
      setOpen(false);
      return;
    }
    if (value.key === key) {
      onChange({ key, direction: value.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      onChange({ key, direction: 'asc' });
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-primary'
        }`}
        title={t('sortBy.title')}
      >
        <ArrowUpDown className="h-4 w-4" />
        <span className="hidden sm:inline">{t('sortBy.buttonLabel')}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-border bg-card py-1 shadow-lg animate-fade-in">
          {visibleOptions.map((opt) => {
            const selected = value.key === opt.key;
            const dirLabel = opt.key !== 'default' && selected
              ? (value.direction === 'asc' ? opt.ascLabel : opt.descLabel)
              : (opt.key !== 'default' ? opt.ascLabel : '');

            return (
              <button
                key={opt.key}
                onClick={() => handleSelect(opt.key)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  selected
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <span className="w-4 shrink-0">
                  {selected && opt.key !== 'default' ? (
                    value.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                  ) : selected ? (
                    <Check className="h-4 w-4" />
                  ) : null}
                </span>
                <span className="flex-1 text-left">{opt.label}</span>
                {dirLabel && (
                  <span className="text-xs text-muted-foreground">{dirLabel}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
